<template>
  <div class="xp-font">
    <!-- Toolbar -->
    <div class="flex items-center gap-2 border-b border-[#d4d0c8] bg-[var(--xp-face)] px-1 py-1">
      <button
        class="flex items-center gap-1 rounded px-2 py-1 text-[12px] text-black disabled:opacity-40"
        :disabled="isRoot"
        @click="goRoot"
      >
        <span
          class="grid h-6 w-6 place-items-center rounded-full border border-[#3b6ea5] bg-gradient-to-b from-[#7db9e8] to-[#1c5b9e] text-sm text-white shadow"
          aria-hidden="true"
        >
          ←
        </span>
        {{ t('xp.explorer.back') }}
      </button>
      <span class="text-[12px] text-[#6d6d6d]">{{ t('xp.explorer.address') }}</span>
      <div class="xp-sunken flex-1 truncate px-2 py-1 font-mono text-[12px] text-black">
        {{ address }}
      </div>
    </div>

    <!-- Root: company folders -->
    <div v-if="nav.kind === 'root'" class="grid grid-cols-2 gap-1 p-2 sm:grid-cols-3">
      <button
        v-for="entry in experience"
        :key="entry.company"
        class="xp-explorer-item flex flex-col items-center gap-1 p-3"
        @click="openCompany(entry.company)"
      >
        <span class="text-[40px] leading-none" aria-hidden="true">📁</span>
        <span class="text-center text-[12px] text-black">{{ entry.company }}</span>
        <span class="text-center font-mono text-[10px] text-[#6d6d6d]">{{ entry.period }}</span>
      </button>
    </div>

    <!-- Company: role .txt files -->
    <div v-else-if="nav.kind === 'company' && company" class="grid grid-cols-2 gap-1 p-2 sm:grid-cols-3">
      <button
        v-for="(role, i) in company.roles"
        :key="role.period"
        class="xp-explorer-item flex flex-col items-center gap-1 p-3"
        @click="openFile(company!.company, i)"
      >
        <span class="text-[40px] leading-none" aria-hidden="true">📄</span>
        <span class="text-center text-[12px] text-black">{{ fileName(role.title) }}</span>
        <span class="text-center font-mono text-[10px] text-[#6d6d6d]">{{ role.period }}</span>
      </button>
    </div>

    <!-- File: notepad content -->
    <div v-else-if="nav.kind === 'file' && file" class="p-2">
      <div class="xp-sunken max-h-[46vh] overflow-y-auto p-3">
        <p class="font-mono text-[13px] font-bold text-black">{{ loc(file.title) }}</p>
        <p class="font-mono text-[11px] text-[#6d6d6d]">{{ companyName }} · {{ file.period }}</p>
        <ul class="mt-2 space-y-1.5">
          <li v-for="(b, i) in file.bullets" :key="i" class="font-mono text-[12px] text-black">
            • {{ loc(b) }}
          </li>
        </ul>
      </div>
    </div>

    <!-- Status bar -->
    <div class="mt-1 flex border-t border-[#d4d0c8] bg-[var(--xp-face)] text-[11px] text-black">
      <span class="flex-1 border-r border-[#d4d0c8] px-2 py-0.5">
        {{ t('xp.explorer.objects', { n: objectCount }) }}
      </span>
      <span class="px-2 py-0.5 font-mono">{{ address }}</span>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue'
import { useI18n } from 'vue-i18n'
import { experience, loc, type ExperienceRole, type LocalizedString } from '../data'

type Nav =
  | { kind: 'root' }
  | { kind: 'company'; company: string }
  | { kind: 'file'; company: string; role: number }

const emit = defineEmits<{
  navigate: [label: string]
}>()

const { t } = useI18n()
const nav = ref<Nav>({ kind: 'root' })

const isRoot = computed(() => nav.value.kind === 'root')

const company = computed(() =>
  nav.value.kind === 'root'
    ? undefined
    : experience.find((e) => e.company === nav.value.company),
)

const companyName = computed(() => company.value?.company ?? '')

const file = computed<ExperienceRole | undefined>(() => {
  if (nav.value.kind !== 'file') return undefined
  return company.value?.roles[nav.value.role]
})

const address = computed(() => {
  const root = t('xp.explorer.myExperience')
  if (nav.value.kind === 'root') return root
  if (nav.value.kind === 'company') return `${root}\\${nav.value.company}`
  const role = company.value?.roles[nav.value.role]
  return `${root}\\${nav.value.company}\\${role ? fileName(role.title) : ''}`
})

const objectCount = computed(() => {
  if (nav.value.kind === 'root') return experience.length
  if (nav.value.kind === 'company') return company.value?.roles.length ?? 0
  return file.value?.bullets.length ?? 0
})

function fileName(title: LocalizedString): string {
  const base = loc(title, 'en').replace(/[\\/:*?"<>|]/g, '').trim().toLowerCase().replace(/\s+/g, '-')
  return `${base || 'role'}.txt`
}

function openCompany(name: string): void {
  nav.value = { kind: 'company', company: name }
  emit('navigate', name)
}

function openFile(companyNameValue: string, role: number): void {
  const entry = experience.find((e) => e.company === companyNameValue)
  const roleTitle = entry?.roles[role]
  nav.value = { kind: 'file', company: companyNameValue, role }
  emit('navigate', roleTitle ? fileName(roleTitle.title) : '')
}

function goRoot(): void {
  nav.value = { kind: 'root' }
  emit('navigate', '')
}
</script>

<style scoped>
.xp-explorer-item {
  border: 1px dotted transparent;
}
.xp-explorer-item:hover {
  border-color: #99ccff;
  background: #e5f3ff;
}
</style>
